import React from 'react'
import { useTranslation } from 'react-i18next'
// import { Box } from '../../../atoms/Box'
// import { Icon } from '../../../atoms/Icon'
// import { Grid } from '../../../atoms/Grid'
import { InfoTooltip } from '../../../InfoTooltip'
// import { SectionTitle } from '../../../atoms/SectionTitle'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import {
  TransportScoreCard,
  ScoreHeader,
  ScoreTitle,
  ScoreValue,
  ScoreLabel,
  Box,
  Icon,
  Grid,
  SectionTitle,
  QualityBadge,
  AnalysisMetricsGrid,
  AnalysisMetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  MetricSubtext,
} from '../../../atoms'
import { formatHeadwayMinutes } from '../../Amenities/transitDisplay'
import { useTransportAndAccess } from '../TransportAndAccessContext'

export const TransportAndAccessOverview: React.FC = () => {
  const { t } = useTranslation('transport')
  const {
    scores,
    publicTransportMetrics,
    roadAccessMetrics,
    activeMobilityMetrics,
  } = useTransportAndAccess()

  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} ${t('units.kilometers', { ns: 'transport' })}`
    }
    return `${Math.round(meters)} ${t('units.meters', { ns: 'transport' })}`
  }

  if (!scores) {
    return <div>{t('states.noData')}</div>
  }

  // Get the translated score label based on score value
  const getScoreLabel = (score: number): string => {
    if (score >= 90) return t('scores.scoreLabels.excellent')
    if (score >= 75) return t('scores.scoreLabels.good')
    if (score >= 60) return t('scores.scoreLabels.moderate')
    if (score >= 45) return t('scores.scoreLabels.basic')
    if (score >= 30) return t('scores.scoreLabels.limited')
    return t('scores.scoreLabels.poor')
  }

  // Get the translated quality class label
  const getQualityClassLabel = (qualityClass: string): string => {
    const validClasses = ['A', 'B', 'C', 'D', 'E', 'F']
    if (validClasses.includes(qualityClass)) {
      return t(`scores.qualityClassLabels.${qualityClass}`)
    }
    return t('scores.qualityClassLabels.unknown')
  }

  // Get the translated quality class description
  const getQualityClassDescription = (qualityClass: string): string => {
    const validClasses = ['A', 'B', 'C', 'D', 'E', 'F']
    if (validClasses.includes(qualityClass)) {
      return t(`scores.qualityClassDescriptions.${qualityClass}`)
    }
    return t('scores.qualityClassDescriptions.unknown')
  }

  const mainScores = [
    {
      title: t('scores.publicTransport.title'),
      titleKey: 'publicTransport',
      value: scores.publicTransport,
      icon: 'tram-front',
      color: '#3b82f6',
      details: [
        {
          label: t('scores.details.proximity'),
          value: scores.details.ptProximity,
        },
        {
          label: t('scores.details.frequency'),
          value: scores.details.ptFrequency,
        },
        {
          label: t('scores.details.diversity'),
          value: scores.details.ptDiversity,
        },
      ],
    },
  ]

  // There is a single stop fetch now (both the metric and the list read the
  // same 1500 m radius), so the metric is always the authoritative nearest
  // stop of its type — no more falling back to the wider stop collection.
  const nearestBusStop = publicTransportMetrics?.nearestBusStop ?? null
  const nearestTrainStation =
    publicTransportMetrics?.nearestTrainStation ?? null

  // ATLAS has no headway for ~9% of stops, so the interval is omitted rather
  // than implying a service every zero minutes.
  const stopInterval = (
    headwayMin: number | null | undefined
  ): string | null => {
    if (typeof headwayMin !== 'number') return null
    const minutes = formatHeadwayMinutes(headwayMin)
    return t('publicTransport.everyMinutes', {
      count: Number(minutes),
      minutes,
      defaultValue: minutes === '1' ? 'every minute' : `every ${minutes} min`,
    })
  }

  const keyMetrics = [
    {
      title: t('overview.metrics.ptQualityClass.title'),
      value: publicTransportMetrics?.qualityClass ?? null,
      subtext: publicTransportMetrics?.qualityClass
        ? getQualityClassDescription(publicTransportMetrics.qualityClass)
        : '',
      icon: 'target',
      color: '#8b5cf6',
      isClass: true,
    },
    {
      title: t('overview.metrics.nearestBusStop.title'),
      value: nearestBusStop
        ? formatDistance(nearestBusStop.distance)
        : t('units.na', { ns: 'transport' }),
      subtext: [nearestBusStop?.name, stopInterval(nearestBusStop?.headwayMin)]
        .filter(Boolean)
        .join(' · '),
      icon: 'bus',
      color: '#f59e0b',
    },
    {
      title: t('overview.metrics.nearestTrainStation.title'),
      value: nearestTrainStation
        ? formatDistance(nearestTrainStation.distance)
        : t('units.na', { ns: 'transport' }),
      subtext: [
        nearestTrainStation?.name,
        stopInterval(nearestTrainStation?.headwayMin),
      ]
        .filter(Boolean)
        .join(' · '),
      icon: 'train',
      color: '#ef4444',
    },
    {
      title: t('overview.metrics.highwayAccess.title'),
      value: roadAccessMetrics?.nearestHighwayAccess
        ? formatDistance(roadAccessMetrics.nearestHighwayAccess.distance)
        : t('units.na', { ns: 'transport' }),
      subtext: roadAccessMetrics?.nearestHighwayAccess?.name || '',
      icon: 'highway',
      color: '#10b981',
    },
    {
      title: t('overview.metrics.stopsWithin500m.title'),
      value: publicTransportMetrics?.totalStopsWithin500m || 0,
      subtext: t('overview.metrics.stopsWithin500m.subtext'),
      icon: 'map-pin-filled',
      color: '#3b82f6',
    },
    {
      title: t('overview.metrics.evCharging.title'),
      value: activeMobilityMetrics?.nearestEVStation
        ? formatDistance(activeMobilityMetrics.nearestEVStation.distance)
        : t('units.na', { ns: 'transport' }),
      subtext: activeMobilityMetrics?.nearestEVStation?.name || '',
      icon: 'zap',
      color: '#22c55e',
    },
  ]

  return (
    <>
      <Box mb="24px">
        <Grid columns={2} gap="16px" mobileColumns={1}>
          {mainScores.map((score) => {
            return (
              <TransportScoreCard
                key={score.title}
                variant={
                  score.value >= 80
                    ? 'success'
                    : score.value >= 60
                      ? 'primary'
                      : score.value >= 40
                        ? 'warning'
                        : 'danger'
                }
              >
                <ScoreHeader>
                  <div>
                    <ScoreTitle>
                      {score.title}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={score.title}
                          shortDescription={t(
                            `scores.${score.titleKey}.tooltip.shortDescription`
                          )}
                          calculation={t('scores.common.calculation')}
                          importance={t('scores.common.importance')}
                          source={t('scores.common.source')}
                        />
                      </InfoTooltip>
                    </ScoreTitle>
                    <ScoreLabel>{getScoreLabel(score.value)}</ScoreLabel>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <ScoreValue size="medium">{score.value}</ScoreValue>
                    <ScoreLabel>{t('scores.common.outOf100')}</ScoreLabel>
                  </div>
                </ScoreHeader>

                <Box
                  mt="12px"
                  style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}
                >
                  {score.details.map((detail) => (
                    <Box
                      key={detail.label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}
                    >
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {detail.label}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 500 }}>
                        {detail.value}/100
                      </span>
                    </Box>
                  ))}
                </Box>
              </TransportScoreCard>
            )
          })}
        </Grid>
      </Box>

      <SectionTitle>{t('overview.keyMetrics')}</SectionTitle>
      <AnalysisMetricsGrid>
        {keyMetrics.map((metric) => (
          <AnalysisMetricCard key={metric.title}>
            <MetricHeader>
              <MetricIcon color={metric.color}>
                <Icon name={metric.icon as any} size="small" />
              </MetricIcon>
              <MetricTitle>{metric.title}</MetricTitle>
            </MetricHeader>
            <Box mt="8px">
              {metric.isClass ? (
                metric.value ? (
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <QualityBadge
                      qualityClass={
                        metric.value as 'A' | 'B' | 'C' | 'D' | 'E' | 'F'
                      }
                    >
                      {t('overview.class')} {metric.value}
                    </QualityBadge>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {getQualityClassLabel(metric.value as string)}
                    </span>
                  </Box>
                ) : (
                  <MetricValue>
                    {t('publicTransport.metrics.qualityClass.none')}
                  </MetricValue>
                )
              ) : (
                <MetricValue>{metric.value}</MetricValue>
              )}
              {metric.subtext && (
                <MetricSubtext>{metric.subtext}</MetricSubtext>
              )}
            </Box>
          </AnalysisMetricCard>
        ))}
      </AnalysisMetricsGrid>
    </>
  )
}
